/* eslint-disable react/no-unescaped-entities */

/* eslint-disable max-len */
import Head from 'next/head'
import styled from 'styled-components'

import FooterPageLayout from 'components/FooterPageLayout'

const Header = styled.h5`
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
`

const StyledText = styled.p`
  margin: 0;
  padding: 0;
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  text-align: justify;

  color: ${props => props.theme.colors.black};
  opacity: 0.7;
`

const StyledLink = styled.a`
  text-decoration: none;
  color: ${props => props.theme.colors.purple};
  &:visited {
    color: ${props => props.theme.colors.purple};
  }
`

const TermsOfUse = () => (
  <FooterPageLayout
    imageSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/footer-hero-1.png`}
    title="HomeLister Terms of Service | Learn to Sell Your Home"
  >
    <Head>
      <title>Terms of Service | Learn to Sell Your Home | HomeLister</title>
      <meta
        name="description"
        content="Learn more about the terms of service at HomeLister- everything you need to sell your home online the better way. Sell your home today with HomeLister."
      />
      <meta
        name="keywords"
        content="Terms of Use, Policies, HomeLister"
      />
    </Head>
    <section>
      <Header>Acceptance of Terms</Header>
      <StyledText>
        Welcome to HomeLister Inc. (“HomeLister”), a technology-powered online real estate
        brokerage. By accessing or using the HomeLister websites (“Websites”) (defined to include
        mobile, Websites or otherwise, owned and operated by HomeLister), related data, tools,
        applications and/or related services (collectively referred to as “Services”), you agree to
        be bound by the following terms of use as updated (“Terms of Use”). These Terms of Use
        constitute a binding contract between HomeLister and you. You are responsible for regularly
        reviewing the Terms of Use. HomeLister complaints or questions regarding these Terms of Use
        may be sent to{' '}
        <StyledLink href="mailto:info@homelister.com">info@homelister.com</StyledLink>. If you do
        not wish to be bound by these Terms of Use, including the binding arbitration clause and
        class action waiver contained herein below, you may not access or use any part of the
        HomeLister Websites, use the Services or create an account. Any person accessing the
        HomeLister Websites is referred to as “you.” HomeLister reserves the right, at its sole
        discretion, to modify or discontinue the Services, or any portion thereof, with or without
        notice, and without liability to you.
      </StyledText>
    </section>
    <section>
      <Header>Eligibility and Registration</Header>
      <StyledText>
        You must be at least 18 years old to use the Services. You may choose to create an account
        and provide certain information, including a valid email address and a password. If you want
        to list your home, you will have to register with us. You agree to provide accurate and
        truthful information, and to keep it accurate and updated. It is your responsibility to
        maintain the confidentiality and security of your information, and you agree to notify us if
        there has been any unauthorized use of your information. You may not share your password
        with unaffiliated third parties.
      </StyledText>
      <StyledText>
        By providing your information, you consent to us or other third parties contacting you about
        your interest in the Services by email, phone or through any other contact information you
        have chosen to provide. You may opt out of marketing by following the instructions in our
        <StyledLink
          ui-sref="privacy"
          href="/privacy-policy"
        >
          Privacy Statement.
        </StyledLink>
      </StyledText>
    </section>
    <section>
      <Header>Intellectual Property</Header>
      <StyledText>
        All parts of the Services, including, but not limited to, all applications, information,
        text, messages, images, photos, illustrations, designs, icons, video clips, sounds, files,
        trademarks, copyrighted material, trade dress, software, specifications, literature,
        technical information, advertisements, the selection, compilation, arrangement and
        presentation of all materials, and the Websites, tools and all other content or materials
        (“Content”), are owned and copyrighted by HomeLister and/or third parties, and are protected
        by U.S. and international laws. You agree to abide by all applicable copyright laws, as well
        as copyright notices or restrictions posted on the Services, and you acknowledge that use of
        our Content without our express prior written permission is strictly prohibited. You may use
        the Content of the Services, but you may not alter it or change any copyrighted notices
        without our permission.
      </StyledText>
      <StyledText>
        You may not use any of our Content, trademarks, trade names, service marks, copyrights or
        logos in any manner which creates the impression that such items belong to or are associated
        with you unless you have our written consent and you acknowledge that you have no ownership
        rights in or to any of such items. You may not use your own web design skills to “frame” the
        Websites and pretend they are yours. You may link to the Websites, provided that you
        acknowledge the source and agree that you will not link the Websites to any website
        containing any inappropriate, profane, defamatory, infringing, obscene, indecent or unlawful
        topic, name, material or information, or that violates any intellectual property,
        proprietary, privacy or publicity rights.
      </StyledText>
      <StyledText>
        You agree that HomeLister may, at its sole discretion and at any time, terminate your access
        to and use of the Services, with or without notice. You further agree that use of the
        HomeLister Services will be immediately terminated if you violate these Terms of Use.
      </StyledText>
    </section>
    <section>
      <Header>General Use of the HomeLister Services</Header>
      <StyledText>
        Unless specified otherwise, HomeLister offers you a limited license to access and use the
        Services solely for your personal and non-commercial uses. You agree to access or use the
        HomeLister Services only for legal purposes that are permitted by the Terms of Use.
      </StyledText>
      <StyledText>
        Content is provided through HomeLister AS IS, and you agree that the use of and reliance on
        any Content is at your own risk, and that under no circumstances shall HomeLister be liable
        for any Content or for any loss or damage of any kind incurred as a result of the use of any
        Content made available via the Services.
      </StyledText>
    </section>
    <section>
      <Header>Content You Provide</Header>
      <StyledText>
        To the extent that you post, upload, input, submit or otherwise transmit (collectively
        “Transmit” or “Transmitting,” as appropriate) Content on or through HomeLister, you agree to
        provide true, accurate and complete information, and to refrain from impersonating or
        falsely representing your affiliation with any person or entity. You are entirely
        responsible for all Content that you provide or otherwise make available via HomeLister.
        Unless we indicate otherwise, you grant us, our subsidiaries and affiliates a nonexclusive,
        transferrable, royalty-free, perpetual, irrevocable and fully sub-licensable right to use,
        reproduce, modify, adapt, publish, translate, create derivative works from, distribute, copy
        and display such Content throughout the world in any form. You also warrant and represent
        that you own or otherwise control all of the rights to such Content, including, without
        limitation, all the rights necessary for you to Transmit such Content and to transfer your
        or others’ interests in such Content to HomeLister, and you represent and warrant that the
        Content will not violate the Terms of Use or cause injury to any other person or entity and
        will not constitute material that is unlawful, harmful, threatening, abusive, harassing,
        tortious, defamatory, vulgar, obscene, libelous or otherwise objectionable. We take no
        responsibility and assume no liability for any material, Content, opinion, recommendation or
        advice submitted by you or any third party. We have no obligation to post any Content you
        provide, and reserve the right to post our own versions of that Content (including, but not
        limited to, photos of properties or property descriptions) instead of yours at our sole
        discretion.
      </StyledText>
      <StyledText>
        You assign us the right to pursue enforcement of copyright and other intellectual property
        claims against third parties that have, without authorization, and in violation of the Terms
        of Use, scraped, copied or distributed Content from the Services that has been provided to
        us by you and for which you have not granted such third parties a separate license to use.
      </StyledText>
      <StyledText>
        By Transmitting Content to HomeLister, you grant, and you represent and warrant that you
        have the right to grant, to HomeLister an irrevocable, perpetual, non-exclusive, fully paid,
        worldwide license to use, copy, perform, display and distribute the Content, and to prepare
        derivative works of, or incorporate into other works, the Content, and to grant and
        authorize sublicenses (through multiple tiers) of the foregoing.
      </StyledText>
    </section>
    <section>
      <Header>How HomeLister May Communicate With You</Header>
      <StyledText>
        For purposes of responding to you and providing you with information and notices about your
        account or the Services (such as information about homes you might be interested in or
        buyers who may be interested in your home), you agree that HomeLister may communicate with
        you through the contact information associated with your HomeLister account, including your
        device ID, email, mobile number, telephone or the postal address you provided, if any.
        HomeLister has no liability arising from your failure to maintain accurate contact or other
        information, including, but not limited to, your failure to receive critical information
        about the Services. Through the Services, you may make requests for home tours, real estate
        agent contact, help selling or buying a home, or other requests. By making those requests,
        you authorize HomeLister to share your personal information, including your home search
        history, favorites and saved searches, with a real estate professional. When you make such a
        request to HomeLister, you are extending an express invitation for HomeLister, or another
        appropriate entity or person, to contact you.
      </StyledText>
    </section>
    <section>
      <Header>Applicable Law/Binding Arbitration/Class Action</Header>
      <strong>
        Please read this section carefully – it may significantly affect your legal rights,
        including your right to file a lawsuit in court and to have a jury hear your claims.
      </strong>
      <StyledText>
        By using the Services, you irrevocably agree: (a) to waive all rights to trial in a court
        before a judge or jury on any claim, action or dispute with us or relating in any way to
        your use of the Services or the interpretation, applicability, enforceability or formation
        of these Terms of Use, including, but not limited to, any claim that all or any part of this
        agreement is void or voidable (“Claims”); (b) that all Claims will be determined exclusively
        by final and binding arbitration in Orange County, California, before one arbitrator; and
        (c) that the arbitrator will not have the authority to consolidate the Claims of other users
        of the Services (“Users”) and will not have the authority to fashion a proceeding as a class
        or collective action, or to award relief to a group or class of Users in one arbitration
        proceeding.
      </StyledText>
      <StyledText>
        The arbitration shall be administered by JAMS pursuant to its Comprehensive Arbitration
        Rules and Procedures, and in accordance with the Expedited Procedures in those rules.
        Notwithstanding these rules, however, such proceeding shall be governed by the laws of the
        State of California, Orange County. All parties shall maintain the confidential nature of
        the arbitration proceeding and the award, including the hearing, except as may be necessary
        to prepare for or conduct the arbitration hearing on the merits, or except as may be
        necessary in connection with a court application for a preliminary remedy, a judicial
        challenge to an award or its enforcement, or unless otherwise required by law or judicial
        decision.
      </StyledText>
      <StyledText>
        The arbitrator, and not any federal, state or local court or agency, shall have exclusive
        authority to resolve any Claims. However, nothing in this section shall prevent us from
        enforcing our intellectual property rights and/or remedy unfair competition,
        misappropriation of trade secrets, unauthorized access, fraud or computer fraud, and/or
        industrial espionage in court. Judgment on any arbitration award may be entered in any court
        having jurisdiction. In any arbitration arising out of or related to these Terms of Use, the
        arbitrator shall award to the prevailing party, if any, the costs and attorneys’ fees
        reasonably incurred by the prevailing party in connection with the arbitration. If the
        arbitrator determines a party to be a prevailing party under circumstances where the
        prevailing party won on some but not all of the claims and counterclaims, the arbitrator may
        award the prevailing party an appropriate percentage of the costs and attorneys’ fees
        reasonably incurred by the prevailing party in connection with the arbitration.
      </StyledText>
    </section>
    <section>
      <Header>Disclaimer of Warranties</Header>
      <StyledText>
        YOU AGREE THAT YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK AND ACKNOWLEDGE THAT ALL
        INFORMATION CONTAINED IN THE SERVICES IS PROVIDED “AS IS” AND “AS AVAILABLE,” AND THAT WE
        DISCLAIM ALL WARRANTIES OR GUARANTEES, EITHER EXPRESS OR IMPLIED, AS TO THE SERVICES,
        INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. We make no representations or guarantees
        that the Services are compatible with your equipment or that the Services, or that any
        electronic communications sent by us or our affiliates, are error-free or will be free from
        loss, destruction, damage, interruption, corruption, attack, viruses, worms or other
        harmful, invasive or corrupted files, interference, hacking or other security intrusion, and
        we disclaim any liability relating thereto. You agree that we have the right to change the
        Content or technical specifications of any aspect of the Services at any time at our sole
        discretion. You further agree that such changes may result in your being unable to access
        the Services. We make no guarantees, representations or warranties that the Services or
        information available through the Services, or that the use of or result of the use of the
        Services, will be accurate, reliable, complete, current, uninterrupted or without errors.
        <strong>
          Any documents, pictures or other information available on the Services are for
          informational purposes only and may not represent the current condition of a property or
          the condition of the property at the time of sale.
        </strong>
        The posting of pictures on the Services does not constitute a guarantee that any items
        represented in the pictures will be present when a buyer takes possession of a property. You
        are encouraged to conduct your own due diligence and investigate all matters relating to any
        properties.{' '}
        <strong>
          It is recommended that you seek independent advice, including legal advice, to perform
          your due diligence and that you use good-faith efforts in determining that the content of
          all information provided to or obtained by you is accurate.
        </strong>
      </StyledText>
      <StyledText>
        Some of the available content, services and information may include materials that belong to
        third parties. You acknowledge that we assume no responsibility for such content, services
        or information. The content of other websites, services, goods or advertisements that may be
        linked to or from the Services is not maintained or controlled by us. We do not: (a) make
        any warranty, express or implied, with respect to the use of the links provided on or to the
        Services; (b) guarantee the accuracy, completeness, usefulness or adequacy of any other
        websites, services or goods, that may be linked to or from the Services; or (c) make any
        endorsement of any other websites, services or goods that may be linked to or from the
        Services.
      </StyledText>
    </section>
    <section>
      <Header>Limitation of Liability</Header>
      <StyledText>
        UNDER NO CIRCUMSTANCES, INCLUDING, BUT NOT LIMITED TO, NEGLIGENCE, SHALL HOMELISTER, ITS
        SUBSIDIARIES OR AFFILIATES BE LIABLE TO YOU OR ANY THIRD PARTY FOR DIRECT, INDIRECT,
        INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE OR EXEMPLARY DAMAGES ARISING FROM USE OF OR
        INABILITY TO USE THE SERVICES, INCLUDING THOSE CAUSED BY ANY FAILURE OF PERFORMANCE, ERROR,
        OMISSION, INTERRUPTION, DEFECT, DELAY IN OPERATION OF TRANSMISSION, COMPUTER VIRUS OR LINE
        FAILURE ARISING OUT OF OR IN ANY WAY CONNECTED WITH YOUR USE OF THE SERVICES, WHETHER BASED
        ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, EVEN IF HOMELISTER HAS BEEN
        ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. ANY SERVICES OR CONTENT MADE AVAILABLE OR
        OBTAINED THROUGH THE USE OF HOMELISTER, AND ALL OTHER USE OF HOMELISTER, IS DONE AT YOUR OWN
        DISCRETION AND RISK, AND YOU WILL BE SOLELY RESPONSIBLE FOR ANY LOSS THAT RESULTS THEREFROM.
        APPLICABLE LAW MAY NOT ALLOW THE LIMITATION OR EXCLUSION OF LIABILITY OR INCIDENTAL OR
        CONSEQUENTIAL DAMAGES, SO THE ABOVE EXCLUSIONS AND LIMITATIONS MAY OR MAY NOT APPLY TO YOU.
        TO THE EXTENT PERMITTED BY LAW, OUR AGGREGATE LIABILITY FOR DAMAGES IN CONNECTION WITH THE
        SERVICES WILL NOT BE GREATER THAN THE AMOUNT YOU PAID TO ACCESS THE SERVICES OR ONE HUNDRED
        DOLLARS ($100), WHICHEVER IS GREATER.
      </StyledText>
    </section>
    <section>
      <Header>Indemnification</Header>
      <StyledText>
        You agree to indemnify, defend and hold HomeLister, our subsidiaries, affiliates,
        third-party vendors, directors, officers, employees and agents harmless, including costs,
        liabilities and legal fees, from any and all claims or demands made by any third party
        arising out of or relating to: (i) your access to or use of the Services; (ii) your
        violation of any third-party right, including, without limitation, any copyright, property
        or privacy right; (iii) the content you submit to the Services; or (iv) any breach by you or
        your affiliates, employees, agents and representatives of the Terms of Services. We reserve
        the right, at your expense, to assume the exclusive defense and control of any matter
        subject to indemnification by you, and you agree to cooperate in such defense. You agree not
        to settle any matter in which you have indemnity obligations without our prior written
        consent. We will use reasonable efforts to notify you of any such claim, action or
        proceeding upon becoming aware of it.
      </StyledText>
    </section>
    <section>
      <Header>Release</Header>
      <StyledText>
        You release HomeLister, its affiliates and their respective directors, officers, employees
        and agents from all liability related to any and all claims and demands you may assert
        against any third party arising out of the Services. If you are a California resident, you
        waive California Civil Code Section 1542, which states, "A general release does not extend
        to claims which the creditor does not know or suspect to exist in his or her favor at the
        time of executing the release, which if known by him or her must have materially affected
        his or her settlement with the debtor."
      </StyledText>
    </section>
    <section>
      <Header>Interpretation of the Terms of Use</Header>
      <StyledText>
        If any part of the Terms of Use is determined to be invalid or unenforceable pursuant to
        applicable law, including, but not limited to, the warranty disclaimers and liability
        limitations contained herein, then the invalid or unenforceable provision will be deemed
        superseded by a valid, enforceable provision that most closely matches the intent of the
        original provision, and the remainder of the Terms of Use shall continue in effect. Unless
        otherwise specified herein (or in any other definitive written agreement between you and
        HomeLister), the Terms of Use constitutes the entire agreement between you and HomeLister
        with respect to the Services, and it supersedes all prior or contemporaneous communications
        and proposals, whether electronic, oral or written, between you and HomeLister with respect
        to the Services. A printed version of the Terms of Use and of any notice given in electronic
        form shall be admissible in judicial or administrative proceedings based upon or relating to
        the Terms of Use to the same extent and subject to the same conditions as other business
        documents and records originally generated and maintained in printed form.
      </StyledText>
    </section>
    <section>
      <Header>MLS Terms of Use</Header>
      <StyledText>
        Before you may view pictures and prices of sold homes or read comments on active listings,
        our multiple listing service (“MLS”) data providers require you to acknowledge that you are
        considering HomeLister as your real estate agent. You have no obligation to work with
        HomeLister or to buy or sell a home. You can choose to work with HomeLister or not. You
        acknowledge and agree that: (a) you are entering into a consumer-broker relationship with
        HomeLister as defined by applicable state law and that you have no obligation to work with
        HomeLister, and you may terminate your account with HomeLister at any time; (b) you have a
        bona fide interest in the purchase, sale or lease of real estate on the HomeLister Website;
        (c) you will not copy, redistribute or retransmit any of the information provided except in
        connection with your consideration of the purchase or sale of an individual property; (d)
        you acknowledge that the individual MLS which supplies the listing data owns such data, and
        you acknowledge the validity of the MLS’s copyright to such data; (e) HomeLister also
        explicitly authorizes MLS employees, MLS members or their duly authorized representatives to
        access HomeLister’s Website for the purpose of verifying compliance with MLS rules and
        monitoring the display of listings posted by users of HomeLister.
      </StyledText>
      <StyledText>
        Additional disclosures apply to residents of Colorado, North Carolina, Rhode Island, South
        Carolina and Texas.
      </StyledText>
    </section>
  </FooterPageLayout>
)

export default TermsOfUse
